import React, { useEffect, useState } from "react";

const PracticeControlsPanel = ({
  isgranted,
  getMicrophonePermission,
  practiceMode,
  recordingStatus,
  recordingPhase = "idle", // "idle" | "recording" | "processing"
  isPlayingSegment,
  recordingProgress,
  handlePlayPause,
  handleMainAction,
  getButtonConfig,
  rate,
  setRate,
  audioref,
  showSpeedControls = true,
  compact = false,

  echoingPhase = "idle",
  shadowCountdownActive = false,
}) => {
  const buttonConfig = getButtonConfig?.() || {
    text: "Start",
    color: "from-blue-500 to-indigo-500",
    icon: "▶️",
    disabled: false,
  };

  const [pulse, setPulse] = useState(0);

  const isProcessing = recordingPhase === "processing";
  const isRecording = recordingStatus === "recording";

  const isEchoCountdown =
    practiceMode === "echoing" && echoingPhase === "paused" && !isProcessing;

  const isShadowCountdown =
    practiceMode === "shadowing" &&
    shadowCountdownActive &&
    !isRecording &&
    !isProcessing;

  const showCueDots = isEchoCountdown || isShadowCountdown;

  useEffect(() => {
    if (!showCueDots) {
      setPulse(0);
      return;
    }

    setPulse(0);

    const t1 = setTimeout(() => setPulse(1), 180);
    const t2 = setTimeout(() => setPulse(2), 520);
    const t3 = setTimeout(() => setPulse(3), 920);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [showCueDots]);

  const processingSurface =
    "bg-gradient-to-r from-indigo-500 via-purple-500 to-blue-500 text-white cursor-wait animate-pulse";

  const processingIcon = (
    <span className="relative flex h-5 w-5 items-center justify-center">
      <span className="absolute inline-flex h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
    </span>
  );

  const renderCueDots = () => (
    <div className="relative z-10 flex items-center gap-1 ml-2">
      <div
        className={`w-2 h-2 rounded-full ${
          pulse >= 1 ? "bg-amber-300" : "bg-white/35"
        }`}
      />
      <div className="w-3 h-0.5 bg-white/25" />
      <div
        className={`w-2 h-2 rounded-full ${
          pulse >= 2 ? "bg-orange-300" : "bg-white/35"
        }`}
      />
      <div className="w-3 h-0.5 bg-white/25" />
      <div
        className={`w-2 h-2 rounded-full ${
          pulse >= 3 ? "bg-green-300 animate-pulse" : "bg-white/35"
        }`}
      />
    </div>
  );

  const getMainLabel = () => {
    if (isProcessing) return "Processing...";
    if (isShadowCountdown || isEchoCountdown) return "Get ready...";

    if (practiceMode === "echoing") {
      if (buttonConfig.text === "Start Echoing") return "Start Echoing";
      if (buttonConfig.text === "Playing...") return "Cancel Playing";
      if (buttonConfig.text === "Get Ready...") return "Cancel Countdown";
      if (buttonConfig.text === "Stop Recording") return "Stop Recording";
    }

    return buttonConfig.text;
  };

  return (
    <div
      className={`p-2 xl:p-5 bg-white/80 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg shadow-blue-500/5 ${
        compact ? "space-y-2" : "space-y-4"
      }`}
    >
      <div className="flex flex-col items-center">
        <div className="w-full max-w-md">
          {!isgranted ? (
            <button
              onClick={getMicrophonePermission}
              className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl transition-all duration-300 font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 flex items-center justify-center gap-3"
            >
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-lg">🎤</span>
              </div>
              <span className="font-medium text-lg">Enable Microphone</span>
            </button>
          ) : practiceMode === "manual" ? (
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handlePlayPause}
                disabled={isRecording || isProcessing}
                className={`flex-1 py-4 px-4 rounded-xl transition-all duration-300 font-medium shadow-lg flex items-center justify-center gap-2 ${
                  isRecording || isProcessing
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : isPlayingSegment
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
                    : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                }`}
              >
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg">{isPlayingSegment ? "⏹️" : "▶️"}</span>
                </div>
                <span className="font-medium">{isPlayingSegment ? "Stop" : "Play"}</span>
              </button>

              <div className="relative flex-1">
                <button
                  onClick={handleMainAction}
                  disabled={!isgranted || isProcessing}
                  className={`w-full py-4 px-4 rounded-xl transition-all duration-300 font-medium shadow-lg flex items-center justify-center gap-2 relative overflow-hidden ${
                    isProcessing
                      ? processingSurface
                      : isRecording
                      ? "bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600"
                      : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  }`}
                >
                  {isRecording && !isProcessing && (
                    <>
                      <div className="absolute inset-0 bg-white/10">
                        <div
                          className="absolute inset-y-0 left-0 bg-gradient-to-r from-white/15 to-white/5 transition-all duration-300"
                          style={{ width: `${recordingProgress}%` }}
                        />
                      </div>
                      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white/10 rounded-b-xl overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-white/40 to-white/30 transition-all duration-300 rounded-b-xl"
                          style={{ width: `${recordingProgress}%` }}
                        />
                      </div>
                    </>
                  )}

                  <div className="relative z-10 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    {isProcessing ? (
                      processingIcon
                    ) : (
                      <span className="text-lg">{isRecording ? "⏹️" : "⏺️"}</span>
                    )}
                  </div>

                  <span className="relative z-10 font-medium">
                    {isProcessing ? "Processing..." : isRecording ? "Stop" : "Record"}
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <div className="relative w-full">
              <button
                onClick={handleMainAction}
                disabled={
                  isProcessing
                    ? true
                    : practiceMode === "echoing" && buttonConfig.text === "Playing..."
                    ? false
                    : buttonConfig.disabled || isEchoCountdown || isShadowCountdown
                }
                className={`w-full py-4 px-6 rounded-xl transition-all duration-300 font-medium shadow-lg flex items-center justify-center gap-3 relative overflow-hidden ${
                  isProcessing
                    ? processingSurface
                    : isShadowCountdown || isEchoCountdown
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                    : practiceMode === "echoing" && buttonConfig.text === "Playing..."
                    ? "bg-gradient-to-r from-blue-400 to-indigo-400 text-white cursor-pointer hover:from-blue-500 hover:to-indigo-500"
                    : buttonConfig.disabled
                    ? "bg-gradient-to-r from-gray-300 to-gray-400 text-gray-500 cursor-not-allowed"
                    : `bg-gradient-to-r ${buttonConfig.color} text-white hover:shadow-xl`
                }`}
              >
                {practiceMode === "echoing" && isRecording && !isProcessing && (
                  <div className="absolute inset-0 bg-white/10">
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-white/15 to-white/5 transition-all duration-300"
                      style={{ width: `${recordingProgress}%` }}
                    />
                  </div>
                )}

                <div className="relative z-10 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  {isProcessing ? (
                    processingIcon
                  ) : (
                    <span className="text-lg">
                      {isShadowCountdown || isEchoCountdown ? "⏳" : buttonConfig.icon}
                    </span>
                  )}
                </div>

                <span className="relative z-10 font-medium text-lg">
                  {getMainLabel()}
                </span>

                {!isProcessing && showCueDots && renderCueDots()}

                {practiceMode === "echoing" && isRecording && !isProcessing && (
                  <span className="relative z-10 ml-2 text-sm font-bold text-white/90">
                    {Math.round(recordingProgress)}%
                  </span>
                )}
              </button>
            </div>
          )}
        </div>

        {showSpeedControls && (
          <div className={`flex items-center space-x-3 ${compact ? "mt-2" : "mt-4"}`}>
            <span className="text-sm text-gray-600 font-medium">Playback Speed:</span>
            <div className="flex space-x-1 bg-white/60 rounded-lg p-1">
              {[0.25, 0.5, 1].map((speed) => (
                <button
                  key={speed}
                  onClick={() => {
                    setRate(speed);
                    if (audioref.current) audioref.current.playbackRate = speed;
                  }}
                  disabled={isProcessing}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                    rate === speed
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow"
                      : "text-gray-600 hover:bg-white"
                  } ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PracticeControlsPanel;